import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import { OrderItemsFields } from './OrderItemsFields';
import { formatNumber } from './orders-format';
import {
  ORDER_FORM_FIELDS,
  createOrderPayload,
  emptyOrderForm,
  orderItemsTotal,
  totalFieldFromItems,
  type OrderFormState,
  type OrderItemRow,
} from './orders-payload';
import { ORDER_LIMITS, validateOrderForm } from './orders-validation';
import { useCreateOrder } from './use-orders';

/**
 * Alta de una orden a mano (`POST .../orders`, solo ADMIN).
 *
 * El 99% de las órdenes nacen del chat (el bot manda el JSON final cuando el cliente confirma el
 * resumen), pero hay ventas que se cierran por teléfono o presencialmente: sin este formulario
 * quedarían fuera del panel, y con ellas los ingresos y la trazabilidad del despacho.
 *
 * Dos reglas del contrato que se respetan a propósito:
 *  - la orden nace `PENDIENTE_CONFIRMACION` (la del bot nace `CONFIRMADA`), así que aquí se carga lo
 *    que pidió el cliente y el estado se confirma después, con el diálogo de cambio de estado;
 *  - `total` es obligatorio y se calcula solo con las líneas (cantidad × precio unitario). Mientras
 *    el admin no lo toque se recalcula en cada cambio; si lo escribe a mano, manda su número (hay
 *    pedidos con envío incluido o descuento que no salen de la suma de los productos).
 */

export interface OrderDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Bloque del formulario: título, ayuda, contenido y el error del bloque (si lo hay). */
function Section({
  title,
  hint,
  error,
  children,
}: {
  title: string;
  hint: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-panel border border-line p-4">
      <div className="space-y-0.5">
        <h3 className="text-body font-medium text-ink">{title}</h3>
        <p className="text-caption text-ink-muted">{hint}</p>
      </div>

      {children}

      {error ? <p className="text-micro text-danger-strong">{error}</p> : null}
    </section>
  );
}

export function OrderDialog({ open, onClose }: OrderDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const createOrder = useCreateOrder(tenantId);

  const [form, setForm] = useState<OrderFormState>(emptyOrderForm);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Mientras nadie toque el total, se recalcula solo con las líneas del pedido. */
  const [totalAuto, setTotalAuto] = useState(true);

  /** Cada apertura arranca limpia (un pedido nuevo, no el que se cargó la vez anterior). */
  useEffect(() => {
    if (!open) return;

    setForm(emptyOrderForm());
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setTotalAuto(true);
  }, [open]);

  const saving = createOrder.isPending;
  const subtotal = orderItemsTotal(form.items);

  /** Cambia un campo sin tocar los demás. */
  function patch(changes: Partial<OrderFormState>) {
    setForm((current) => ({ ...current, ...changes }));
  }

  /** Las líneas cambian → si el total está en automático, se recalcula con ellas. */
  function changeItems(items: OrderItemRow[]) {
    setForm((current) => ({
      ...current,
      items,
      ...(totalAuto ? { total: totalFieldFromItems(items) } : {}),
    }));
  }

  /** Devuelve el total al cálculo automático (botón «Recalcular»). */
  function recalcTotal() {
    setTotalAuto(true);
    patch({ total: totalFieldFromItems(form.items) });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateOrderForm(form);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await createOrder.mutateAsync(createOrderPayload(form));
      onClose();
    } catch (error) {
      // El backend valida lo mismo (y más): sus mensajes se reparten entre el campo que los produjo
      // y el aviso general del formulario.
      setErrors(formErrorsFromApiError(error, ORDER_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Nueva orden"
      description="Un pedido que se cerró por fuera del chat. Nace pendiente de confirmación: cuando el cliente te confirme, cámbiala a confirmada."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {/* `form` enlaza este botón del pie con el formulario del cuerpo del modal. */}
          <Button type="submit" form="order-form" loading={saving}>
            Crear orden
          </Button>
        </>
      }
    >
      <form id="order-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        <Section
          title="Cliente"
          hint="Con quién se cerró la venta: este nombre es el que se ve en la tabla y en el aviso de nueva venta."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nombre del cliente"
              htmlFor="orderClienteNombre"
              required
              hint="Como lo llamas tú (ej. «Ana Pérez»)."
              error={fieldError(errors, 'clienteNombre')}
            >
              <Input
                id="orderClienteNombre"
                name="clienteNombre"
                placeholder="Ej. Ana Pérez"
                autoFocus
                maxLength={ORDER_LIMITS.clienteNombre}
                value={form.clienteNombre}
                invalid={Boolean(fieldError(errors, 'clienteNombre'))}
                onChange={(event) => patch({ clienteNombre: event.target.value })}
              />
            </Field>

            <Field
              label="Teléfono"
              htmlFor="orderTelefono"
              hint="Para confirmarle el pedido y para la transportadora."
              error={fieldError(errors, 'telefono')}
            >
              <Input
                id="orderTelefono"
                name="telefono"
                placeholder="Ej. +57 300 111 2233"
                maxLength={ORDER_LIMITS.telefono}
                value={form.telefono}
                invalid={Boolean(fieldError(errors, 'telefono'))}
                onChange={(event) => patch({ telefono: event.target.value })}
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Productos del pedido"
          hint="Cada línea es un producto con su cantidad. El precio unitario es opcional: si lo dejas vacío, el subtotal de esa línea queda «—» y solo cuenta el total."
          error={fieldError(errors, 'productos')}
        >
          <OrderItemsFields rows={form.items} disabled={saving} onChange={changeItems} />
        </Section>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Total del pedido"
            htmlFor="orderTotal"
            required
            hint={
              totalAuto
                ? 'Se calcula con las líneas del pedido. Si un producto no tiene precio, escríbelo aquí a mano.'
                : 'Lo escribiste a mano: usa «Recalcular» para volver a la suma de los productos.'
            }
            error={fieldError(errors, 'total')}
          >
            <Input
              id="orderTotal"
              name="total"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              placeholder="Ej. 90000"
              value={form.total}
              invalid={Boolean(fieldError(errors, 'total'))}
              onChange={(event) => {
                setTotalAuto(false);
                patch({ total: event.target.value });
              }}
            />
          </Field>

          <div className="flex items-end pb-1">
            <Button variant="secondary" size="sm" disabled={saving} onClick={recalcTotal}>
              {`Recalcular (${formatNumber(subtotal)})`}
            </Button>
          </div>
        </div>

        <Section
          title="Entrega"
          hint="A dónde va el pedido. Lo que dejes vacío queda sin dato: el bot no lo inventa y la guía se carga aparte."
          error={
            fieldError(errors, 'departamento') ??
            fieldError(errors, 'ciudad') ??
            fieldError(errors, 'barrio') ??
            fieldError(errors, 'direccion')
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Departamento" htmlFor="orderDepartamento" error={fieldError(errors, 'departamento')}>
              <Input
                id="orderDepartamento"
                name="departamento"
                placeholder="Ej. Valle del Cauca"
                maxLength={ORDER_LIMITS.zona}
                value={form.departamento}
                invalid={Boolean(fieldError(errors, 'departamento'))}
                onChange={(event) => patch({ departamento: event.target.value })}
              />
            </Field>

            <Field label="Ciudad" htmlFor="orderCiudad" error={fieldError(errors, 'ciudad')}>
              <Input
                id="orderCiudad"
                name="ciudad"
                placeholder="Ej. Cali"
                maxLength={ORDER_LIMITS.zona}
                value={form.ciudad}
                invalid={Boolean(fieldError(errors, 'ciudad'))}
                onChange={(event) => patch({ ciudad: event.target.value })}
              />
            </Field>

            <Field label="Barrio" htmlFor="orderBarrio" error={fieldError(errors, 'barrio')}>
              <Input
                id="orderBarrio"
                name="barrio"
                placeholder="Ej. Granada"
                maxLength={ORDER_LIMITS.zona}
                value={form.barrio}
                invalid={Boolean(fieldError(errors, 'barrio'))}
                onChange={(event) => patch({ barrio: event.target.value })}
              />
            </Field>

            <Field label="Dirección" htmlFor="orderDireccion" error={fieldError(errors, 'direccion')}>
              <Input
                id="orderDireccion"
                name="direccion"
                placeholder="Ej. Calle 10 # 4-11, apto 302"
                maxLength={ORDER_LIMITS.direccion}
                value={form.direccion}
                invalid={Boolean(fieldError(errors, 'direccion'))}
                onChange={(event) => patch({ direccion: event.target.value })}
              />
            </Field>
          </div>
        </Section>

        <Section
          title="Pago"
          hint="Cómo se acordó el pago. Es texto libre: escríbelo como lo hablaste con el cliente."
        >
          <Field
            label="Método de pago"
            htmlFor="orderMetodoPago"
            hint="Ej. contraentrega, transferencia, Nequi."
            error={fieldError(errors, 'metodoPago')}
          >
            <Input
              id="orderMetodoPago"
              name="metodoPago"
              placeholder="Ej. Contraentrega"
              maxLength={ORDER_LIMITS.metodoPago}
              value={form.metodoPago}
              invalid={Boolean(fieldError(errors, 'metodoPago'))}
              onChange={(event) => patch({ metodoPago: event.target.value })}
            />
          </Field>
        </Section>

        <Section
          title="Conversación de origen (opcional)"
          hint="Si el pedido salió de un chat, pega el id de la conversación (el «conversacion=<id>» de la URL del inbox): ese contacto pasa a CLIENTE y su asistente a postventa."
          error={fieldError(errors, 'conversationId')}
        >
          <Field
            label="Id de la conversación"
            htmlFor="orderConversationId"
            hint="Déjalo vacío si la venta se cerró por fuera del panel (queda sin atribución)."
            error={fieldError(errors, 'conversationId')}
          >
            <Input
              id="orderConversationId"
              name="conversationId"
              placeholder="Ej. 8f14e45f-ceea-4d0f-9a90-1c2d3e4f5a6b"
              value={form.conversationId}
              invalid={Boolean(fieldError(errors, 'conversationId'))}
              onChange={(event) => patch({ conversationId: event.target.value })}
            />
          </Field>
        </Section>

        <p className="text-micro text-ink-faint">
          La orden nace pendiente de confirmación: no entra a los reportes como venta cerrada hasta
          que la muevas a «Confirmada» (o la canceles).
        </p>
      </form>
    </Modal>
  );
}

